import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'ncoded-component-library';
import { Field, useForm, useFormState } from 'react-final-form';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import ResolvePopup from 'components/ResolvePopup';
import utils, {
  getCheckedValuesInObject,
  getJoinedWordsWithComma,
  getUserFullName,
} from 'utils';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import { BuyerEnquiryHistory, PreviousDeal } from 'models/DealRoom';
import { SORT_VALUES, sortPreviousLeads } from './utils';
import usePastTime from 'hooks/usePastTime';

import './SelectLeads.styles.scss';

type SelectLeadsProps = {
  className?: string;
  leads: BuyerEnquiryHistory[];
};

const SelectLeads: React.FC<SelectLeadsProps> = (props) => {
  const { className, leads } = props;

  const [sort, updateSort] = useState(`-${SORT_VALUES.NUMBER_OF_ENQUIRIES}`);

  const classes = classNames('select-leads', className);

  const { t } = useTranslation();

  const pastTime = usePastTime();

  const {
    values: { leads: formLeads },
  } = useFormState();
  const { change, batch } = useForm();

  const sortedLeads = useMemo(
    () => leads.sort((a, b) => sortPreviousLeads(a, b, sort)),
    [leads, sort],
  );

  const checkedLeadsLength = useMemo(
    () => (getCheckedValuesInObject(formLeads || {}) || []).length,
    [formLeads],
  );

  const nonExistingLeads = useMemo(
    () => leads.filter(({ existLead }) => !existLead),
    [leads],
  );

  const areAllSelected = useMemo(
    () => checkedLeadsLength === nonExistingLeads.length,
    [checkedLeadsLength, nonExistingLeads],
  );

  const onAllSelect = useCallback(
    () =>
      batch(() =>
        nonExistingLeads.forEach(({ buyerId }) =>
          change(`leads.${buyerId}`, !areAllSelected),
        ),
      ),
    [areAllSelected, nonExistingLeads, batch, change],
  );

  const getPreviousEnquiries = useCallback(
    (previousEnquiries: PreviousDeal[]) => (
      <p>
        {getJoinedWordsWithComma(
          previousEnquiries.map(({ dealName }) => dealName),
        )}
      </p>
    ),
    [],
  );

  return (
    <div className={classes}>
      <div className="sorting-buttons">
        <Checkbox checked={areAllSelected} onChange={onAllSelect} />

        <SortingButton
          content={t('lead')}
          sortingValue={SORT_VALUES.LEAD}
          sort={sort}
          updateSort={updateSort}
        />
        <SortingButton
          content={t('numberOfPreviousEnquiries')}
          sortingValue={SORT_VALUES.NUMBER_OF_ENQUIRIES}
          sort={sort}
          updateSort={updateSort}
        />
        <SortingButton
          content={t('lastEnquiry')}
          sortingValue={SORT_VALUES.LAST_ENQUIRE}
          sort={sort}
          updateSort={updateSort}
        />
      </div>

      <div className="leads">
        {sortedLeads.map(
          ({
            buyerId,
            buyerFirstName,
            buyerLastName,
            buyerEmail,
            lastEnquire,
            previousDeals,
            existLead,
          }) => (
            <div key={buyerId}>
              {existLead ? (
                <Checkbox
                  checked={existLead}
                  disabled={existLead}
                  label={
                    <div className="name">
                      {getUserFullName(buyerFirstName, buyerLastName) ||
                        buyerEmail}
                    </div>
                  }
                  onChange={utils.noop}
                />
              ) : (
                <Field
                  name={`leads.${buyerId}`}
                  type="checkbox"
                  disabled={existLead}
                  component={CheckboxField}
                  label={
                    <div className="name">
                      {getUserFullName(buyerFirstName, buyerLastName) ||
                        buyerEmail}
                    </div>
                  }
                />
              )}

              <div className="previous-deals">
                <ResolvePopup
                  onHover
                  popupContent={getPreviousEnquiries(previousDeals)}
                >
                  {previousDeals?.length}
                </ResolvePopup>
              </div>

              <div className="last-enquire">{pastTime(lastEnquire)}</div>
            </div>
          ),
        )}
      </div>

      {!checkedLeadsLength && (
        <Field
          name="leads"
          hidden
          component="input"
          validate={() => t('requiredField')}
        />
      )}
    </div>
  );
};

export default SelectLeads;
